import {SagaIterator} from "redux-saga";
import {call, select, takeLatest, getContext} from "redux-saga/effects";
import {Action, isType} from "typescript-fsa";
import {bindAsyncAction} from "typescript-fsa-redux-saga";
import {IceClient} from "@neolab/ice-client";

import {Ganymede} from "@slices/Ganymede/UserManager";
import {GanymedePermissions} from "@slices/GanymedePermissions/Permissions";
import {Permissions} from "@slices/Permissions/Permissions";
import "@slices/Permissions/PermissionManager";

import {routeSaga} from "src/utils/router";
import {logError} from "src/utils/sagas";
import {RootState} from "src/reducers";

import {
  usersRoute, getUsers, searchFormSubmitted,
  getUserPermissions, userDialogClosed, userClicked, togglePermission,
  permissionToggled,
} from "./Users.actions";


const getUsersWorker = bindAsyncAction(getUsers)(
  function* getUsersWorker({provider, search}): SagaIterator {
    const iceClient: IceClient = yield getContext('iceClient');

    const userManagerPrx: Ganymede.UserManagerPrx =
      yield call(iceClient.getProxy, 'UserManager');

    return yield call(userManagerPrx.getUsers, provider, search);
  },
);

function* searchWorker(): SagaIterator {
  const {provider, search} = yield select((state: RootState) => {
    const {searchProvider: provider, searchText: search} = state.routes.users;
    return {provider, search};
  });

  try {
    yield call(getUsersWorker, {provider, search});
  } catch (e) {
    logError("Failed to load users", e, 'warn');
  }
}


const getUserPermissionsWorker = bindAsyncAction(getUserPermissions)(
  function* getUserPermissionsWorker(user): SagaIterator {
    const iceClient: IceClient = yield getContext('iceClient');

    const permissionManagerPrx: Permissions.PermissionManagerPrx =
      yield call(iceClient.getProxy, 'PermissionManager');

    return yield call(permissionManagerPrx.view,
      user.identity, Permissions.PrincipalKind.User);
  },
);

function findPermission(typeId: string): Permissions.AbstractPermission {
  for (const key of Object.keys(GanymedePermissions)) {
    const value = (GanymedePermissions as any)[key];

    if (value.ice_staticId && value.ice_staticId() === typeId)
      return new value();
  }

  throw new Error(`Could not find permission ${typeId}`);
}

const togglePermissionWorker = bindAsyncAction(togglePermission)(
  function* togglePermissionWorker(
    {typeId, granted}: {typeId: string, granted: boolean},
    user: Ganymede.User,
  ): SagaIterator {
    const iceClient: IceClient = yield getContext('iceClient');

    const permissionManagerPrx: Permissions.PermissionManagerPrx =
      yield call(iceClient.getProxy, 'PermissionManager');

    const permission = findPermission(typeId);

    const principal: Permissions.Principal = yield call(
      granted ? permissionManagerPrx.grant : permissionManagerPrx.revoke,
      user.identity,
      Permissions.PrincipalKind.User,
      permission,
    );

    return principal.permissions;
  },
);

function* dialogWorker(action: Action<any>): SagaIterator {
  if (!isType(action, userClicked))
    return;

  const user = yield select((state: RootState) =>
    state.routes.users.users![action.payload.row]);

  yield takeLatest(permissionToggled.type, (action: Action<any>) =>
    togglePermissionWorker(action.payload, user));

  try {
    yield call(getUserPermissionsWorker, user);
  } catch (e) {
    logError("Failed to load user permissions", e, 'warn');
  }
}

const usersSaga = routeSaga(usersRoute)(
  function* usersSaga(): SagaIterator {
    yield takeLatest(searchFormSubmitted.type, searchWorker);

    yield takeLatest([userClicked.type, userDialogClosed.type], dialogWorker);
  },
);

export default usersSaga;



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/routes/users/saga.ts